// frontend/src/widgets/LoggedInUsersWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-26
// Updated:      2026-09-08 15:11 BST
//
// Description:
//   Widget to display a count of currently logged-in users with an expandable
//   list of usernames.
//
// Lifecycle Status:
//   Active.
//
// AUD8 Follow-up:
//   - Uses the authenticated, tenant-scoped /api/users/logged-in endpoint.
//   - Authentication is handled centrally through axiosInstance.
//   - The widget does not read tokens directly or construct Authorization headers.
//
// Mobile Responsiveness (2026-09-08):
//   - Preserves the existing single-column widget structure.
//   - Uses natural height on mobile while retaining desktop widget fill behaviour.
//   - Makes the expandable summary control a full-width accessible button.
//   - Provides a touch-friendly minimum control height.
//   - Allows usernames and last-active values to wrap safely on narrow screens.
//   - Keeps the expanded user list internally scrollable to avoid excessive widget height.
//   - Uses one render path for desktop and mobile; no duplicated mobile DOM.
//
// Operational Notes:
//   - This widget performs one authenticated request when mounted.
//   - It does not poll automatically.
//   - The endpoint remains tenant-scoped by the authenticated backend context.

import React, { useEffect, useState } from "react";

import axiosInstance from "../api/axiosInstance";

const LoggedInUsersWidget = () => {
  const [users, setUsers] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    axiosInstance
      .get("/users/logged-in")
      .then((res) => {
        if (cancelled) {
          return;
        }

        setUsers(
          Array.isArray(res.data)
            ? res.data
            : []
        );
        setError(null);
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }

        console.error(
          "Failed to load logged-in users:",
          err
        );

        setUsers([]);
        setError(
          "Failed to load users"
        );
      })
      .finally(() => {
        if (cancelled) {
          return;
        }

        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const userCount = users.length;

  return (
    <div className="h-auto min-h-0 w-full overflow-visible rounded-lg border border-gray-200 bg-white shadow-sm md:h-full md:overflow-hidden">
      <div className="p-3 md:p-4">
        <button
          type="button"
          className="flex min-h-[44px] w-full cursor-pointer flex-col items-center justify-center rounded border border-green-400 px-4 py-2 text-center transition hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          onClick={() =>
            setExpanded(
              (current) =>
                !current
            )
          }
          aria-expanded={expanded}
          aria-controls="logged-in-users-list"
        >
          <span className="text-lg font-semibold text-green-600">
            Users Online
          </span>

          <span className="text-sm text-gray-600">
            {userCount}
          </span>
        </button>

        {expanded && (
          <div
            id="logged-in-users-list"
            className="mt-3 max-h-40 overflow-y-auto overflow-x-hidden rounded border border-gray-200 bg-gray-50 p-2"
          >
            {userCount === 0 ? (
              <p className="m-0 text-center text-sm text-gray-500">
                No users currently online.
              </p>
            ) : (
              <ul className="m-0 list-disc space-y-1 pl-5 text-sm text-gray-700">
                {users.map(
                  (user) => (
                    <li
                      key={
                        user.id ||
                        user.username
                      }
                      className="break-words"
                      style={{
                        overflowWrap:
                          "anywhere",
                      }}
                    >
                      <span className="font-medium">
                        {user.username}
                      </span>

                      {user.last_active_at ? (
                        <span className="text-xs text-gray-500">
                          {" "}
                          —{" "}
                          {new Date(
                            user.last_active_at
                          ).toLocaleTimeString()}
                        </span>
                      ) : null}
                    </li>
                  )
                )}
              </ul>
            )}
          </div>
        )}

        {error && (
          <p
            role="alert"
            className="mt-2 mb-0 break-words text-sm text-red-600"
            style={{
              overflowWrap:
                "anywhere",
            }}
          >
            {error}
          </p>
        )}

        {loading && (
          <p
            className="mt-2 mb-0 text-sm text-gray-500"
            aria-live="polite"
          >
            Loading users...
          </p>
        )}
      </div>
    </div>
  );
};

export default LoggedInUsersWidget;
